import React, { useState } from 'react';
import { BriefcaseIcon, ClockIcon, DollarSignIcon, BookOpenIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
interface ProgramContentSectionProps {
  degreeInterest: string;
  areaOfStudy: string;
  specialization: string;
}
// Helper to generate realistic careers based on area of study
const getCareersForArea = (areaValue: string, programName: string) => {
  const defaultCareers = [{
    title: `${programName} Specialist`,
    desc: `Apply your knowledge of ${programName.toLowerCase()} in various professional settings.`,
    salary: '$55,000 - $85,000'
  }, {
    title: 'Project Coordinator',
    desc: 'Manage teams and deliverables across different organizational departments.',
    salary: '$60,000 - $90,000'
  }, {
    title: 'Operations Analyst',
    desc: 'Improve efficiency and processes within your chosen industry.',
    salary: '$65,000 - $95,000'
  }, {
    title: 'Consultant',
    desc: `Provide expert advice on ${programName.toLowerCase()} to businesses and clients.`,
    salary: '$70,000 - $110,000'
  }];
  const careerMap: Record<string, any[]> = {
    '1': [{
      title: 'Financial Analyst',
      desc: 'Guide businesses and individuals in making investment decisions.',
      salary: '$70,000 - $100,000'
    }, {
      title: 'Marketing Manager',
      desc: 'Estimate the demand for products and services and identify potential markets.',
      salary: '$85,000 - $135,000'
    }, {
      title: 'Human Resources Specialist',
      desc: 'Recruit, screen, interview, and place workers.',
      salary: '$55,000 - $80,000'
    }, {
      title: 'Management Analyst',
      desc: "Propose ways to improve an organization's efficiency.",
      salary: '$75,000 - $115,000'
    }],
    '2': [{
      title: 'Police Officer or Detective',
      desc: 'Protect lives and property, and gather facts and evidence for criminal cases.',
      salary: '$60,000 - $90,000'
    }, {
      title: 'Probation Officer',
      desc: 'Provide social services to assist in rehabilitation of law offenders.',
      salary: '$50,000 - $75,000'
    }, {
      title: 'Information Security Analyst',
      desc: 'Plan and carry out security measures to protect computer networks.',
      salary: '$90,000 - $130,000'
    }, {
      title: 'Paralegal',
      desc: 'Support lawyers by organizing files, conducting legal research, and drafting documents.',
      salary: '$45,000 - $70,000'
    }],
    '3': [{
      title: 'Substance Abuse Counselor',
      desc: 'Advise people who suffer from alcoholism, drug addiction, or other behavioral problems.',
      salary: '$45,000 - $65,000'
    }, {
      title: 'Social Worker',
      desc: 'Help people solve and cope with problems in their everyday lives.',
      salary: '$45,000 - $70,000'
    }, {
      title: 'Market Research Analyst',
      desc: 'Study market conditions to examine potential sales of a product or service.',
      salary: '$60,000 - $90,000'
    }, {
      title: 'Human Resources Manager',
      desc: 'Plan, direct, and coordinate the administrative functions of an organization.',
      salary: '$90,000 - $130,000'
    }],
    '4': [{
      title: 'Software Developer',
      desc: 'Create the applications or systems that run on a computer or another device.',
      salary: '$95,000 - $150,000'
    }, {
      title: 'Computer Systems Analyst',
      desc: "Study an organization's current computer systems and design solutions.",
      salary: '$80,000 - $120,000'
    }, {
      title: 'Database Administrator',
      desc: 'Use specialized software to store and organize data.',
      salary: '$85,000 - $125,000'
    }, {
      title: 'Web Developer',
      desc: 'Design and create websites, ensuring performance and capacity.',
      salary: '$70,000 - $110,000'
    }],
    '5': [{
      title: 'Medical and Health Services Manager',
      desc: 'Plan, direct, and coordinate medical and health services.',
      salary: '$85,000 - $130,000'
    }, {
      title: 'Health Educator',
      desc: 'Teach people about behaviors that promote wellness.',
      salary: '$50,000 - $75,000'
    }, {
      title: 'Medical Records Specialist',
      desc: 'Compile, process, and maintain patient files.',
      salary: '$40,000 - $60,000'
    }, {
      title: 'Clinical Laboratory Technologist',
      desc: 'Collect samples and perform tests to analyze body fluids, tissue, and other substances.',
      salary: '$50,000 - $75,000'
    }],
    '10': [{
      title: 'Registered Nurse (RN)',
      desc: 'Provide and coordinate patient care and educate patients about various health conditions.',
      salary: '$70,000 - $100,000'
    }, {
      title: 'Nurse Practitioner',
      desc: 'Coordinate patient care and may provide primary and specialty healthcare.',
      salary: '$100,000 - $140,000'
    }, {
      title: 'Medical Manager',
      desc: 'Direct and coordinate the business activities of healthcare providers.',
      salary: '$90,000 - $130,000'
    }, {
      title: 'Clinical Nurse Specialist',
      desc: 'Provide direct patient care and expert consultations.',
      salary: '$95,000 - $135,000'
    }]
  };
  return careerMap[areaValue] || defaultCareers;
};
export function ProgramContentSection({
  degreeInterest,
  areaOfStudy,
  specialization
}: ProgramContentSectionProps) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  // Lookup display names
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const degreeName = degreeNameMap[degreeInterest] || 'Degree';
  const areaName = focusOptions.find(f => f.value === areaOfStudy)?.type || 'your field';
  const programName = specializationOptions.find(s => s.value === specialization)?.type || areaName;
  const careers = getCareersForArea(areaOfStudy, programName);
  const faqs = [{
    q: `How long does it take to complete an online ${degreeName} in ${programName}?`,
    a: `The duration depends on your chosen pace and transfer credits. Typically, an online ${degreeName} program takes anywhere from 1 to 4 years. Many online programs offer accelerated 8-week courses that allow you to finish faster than traditional 16-week semesters.`
  }, {
    q: `What are the admission requirements for a ${programName} program?`,
    a: `Admission requirements vary by school, but generally include a high school diploma or GED (for undergraduate programs) or a bachelor's degree (for graduate programs). Some programs may require minimum GPAs, letters of recommendation, or relevant work experience.`
  }, {
    q: `Is an online ${programName} degree respected by employers?`,
    a: `Yes. Employers increasingly view online degrees from accredited institutions exactly the same as traditional on-campus degrees. What matters most is the accreditation of the school and the skills you acquire, not the medium of instruction.`
  }, {
    q: `Can I transfer previous college credits toward my ${programName} degree?`,
    a: `Most accredited online universities have generous transfer credit policies. They will evaluate your previous college transcripts, military training, and sometimes even work experience to grant credits, which can significantly reduce your tuition costs and time to graduation.`
  }];
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  return <div className="bg-white border-t border-gray-200 mt-12 pt-16 pb-20" data-id="element-450" data-mp="3axkthhu045np">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-451" data-mp="3avi4900oagk1">
        {/* Program Overview */}
        <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-452" data-mp="3fu1qz2n3bwdg">
          <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-453" data-mp="1gklg7vd3fnjl">
            What is an Online {programName} {degreeName}?
          </h2>
          <div className="prose prose-lg text-gray-600 mx-auto leading-relaxed space-y-6" data-id="element-454" data-mp="1lkhct9jgocp4">
            <p data-id="element-455" data-mp="3ndu85jrj932t">
              Earning an online {degreeName} in {programName} prepares you with
              the specialized knowledge and practical skills needed to succeed
              in today's competitive job market. This program focuses on the
              core principles of {areaName.toLowerCase()}, combining theoretical
              foundations with real-world applications.
            </p>
            <p data-id="element-456" data-mp="34j5is5g9l18r">
              Designed for working adults and non-traditional students, online
              programs offer the flexibility to study on your own schedule
              without sacrificing quality. You'll learn from experienced
              faculty, engage with peers through digital platforms, and develop
              a comprehensive understanding of {programName.toLowerCase()} that
              you can immediately apply to your career.
            </p>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="mb-20" data-id="element-457" data-mp="26na6u11lpz25">
          <div className="text-center mb-10" data-id="element-458" data-mp="26m06f57a0esc">
            <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-459" data-mp="2bmefmqkm5ury">
              Career Opportunities with a {degreeName} in {programName}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-460" data-mp="1xog3zsxogcg5">
              Graduates with a background in {programName.toLowerCase()} are
              well-positioned for a variety of rewarding roles. Here are some
              potential career paths:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-461" data-mp="2ubugxg44hr9f">
            {careers.map((career, idx) => <div key={idx} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200 flex flex-col h-full items-center justify-start" data-id="element-462" data-mp="2gl7scgs2thr2" data-mpi={idx}>
                <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-463" data-mp="399mva4xxo9m1">
                  <BriefcaseIcon className="w-6 h-6" data-id="element-464" data-mp="3e2g2czgvju1a" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-2 leading-tight" data-id="element-465" data-mp="k1dcjoi04s08">
                  {career.title}
                </h3>
                <p className="text-sm text-gray-600 mb-6 flex-grow" data-id="element-466" data-mp="owsq0fc3klw4">
                  {career.desc}
                </p>
                <div className="pt-4 border-t border-gray-200 mt-auto" data-id="element-467" data-mp="f0pc6c7vuytc">
                  <div className="flex items-center text-sm font-semibold text-gray-800" data-id="element-468" data-mp="f323vh3g0njl">
                    <DollarSignIcon className="w-4 h-4 text-[#059668] mr-1" data-id="element-469" data-mp="189elqxr3szt" />
                    {career.salary}
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-470" data-mp="3nh2dqgn5vesn">
                    Estimated Annual Salary
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Why Choose Online */}
        <div className="mb-20 bg-gray-50 rounded-2xl p-8 sm:p-12 border border-gray-200" data-id="element-471" data-mp="3eh5jlzi05nq7">
          <div className="text-center mb-12" data-id="element-472" data-mp="3jfgvsgvrsabl">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1B365D] mb-4" data-id="element-473" data-mp="3jh9y5jzco8ds">
              Why Earn Your {programName} {degreeName} Online?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-474" data-mp="2uvc9r3p3b6rw">
              Online education has transformed how adults achieve their academic
              goals, offering distinct advantages over traditional on-campus
              learning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-475" data-mp="3irq4bh0wgs24">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-476" data-mp="3ipwxa0mdk5tl">
              <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-477" data-mp="3dr2gf47ywrc4">
                <ClockIcon className="w-6 h-6 text-[#1B365D]" data-id="element-478" data-mp="3sk5q6pfbi0sa" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-479" data-mp="3njrorq881p38">
                Ultimate Flexibility
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-480" data-mp="5b0pd44zkmn1">
                Study when and where it works for you. Balance your education
                with your current job, family responsibilities, and personal
                life without commuting.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-481" data-mp="2z2yzhk2ifp6y">
              <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-482" data-mp="334txael8fkfz">
                <TrendingUpIcon className="w-6 h-6 text-[#1B365D]" data-id="element-483" data-mp="37zhpoojcwyzu" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-484" data-mp="3cyu7uvza9hw3">
                Career Advancement
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-485" data-mp="3muxjcsg0ysvp">
                Apply what you learn immediately to your current role. Show
                employers your dedication and time-management skills by
                completing a rigorous online program.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-486" data-mp="1wi83vvcr9uvd">
              <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-487" data-mp="1oyywu8rq289h">
                <GlobeIcon className="w-6 h-6 text-[#1B365D]" data-id="element-488" data-mp="1k8iimmg2uim2" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-489" data-mp="1k6fgx55kb11i">
                Broader Options
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-490" data-mp="1b7u0bfjnhw3d">
                Don't limit yourself to local schools. Access top-tier{' '}
                {programName} programs from accredited universities nationwide,
                finding the perfect fit for your goals.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto" data-id="element-491" data-mp="1yyziw2gz72hd">
          <div className="text-center mb-10" data-id="element-492" data-mp="1tyuyw2guzpme">
            <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-493" data-mp="1u062a16e6vcz">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600" data-id="element-494" data-mp="iiwmbomh2lgf">
              Common questions about earning your {programName} degree online.
            </p>
          </div>

          <div className="space-y-4" data-id="element-495" data-mp="26e1ha91l4c7v">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-200" data-id="element-496" data-mp="2b6unl1cpbsm2" data-mpi={index}>
                <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-navy" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index} data-id="element-497" data-mp="1xgr5mmkuul2f">
                  <span className="font-semibold text-navy pr-8" data-id="element-498" data-mp="2u56t8d4emkcw">{faq.q}</span>
                  {openFaqIndex === index ? <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-499" data-mp="2g5nzipccbbk4" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-500" data-mp="2g7ib3d1q1rbj" />}
                </button>

                {openFaqIndex === index && <div className="px-6 pb-5 pt-0" data-id="element-501" data-mp="14ppls867w1ck">
                    <p className="text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4 mt-2" data-id="element-502" data-mp="14oxnuqp5drj4">
                      {faq.a}
                    </p>
                  </div>}
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}